/**
 * Orders API Types - Zod Schemas & DTOs
 * Anti-corruption layer: validates and types API responses
 */

import { z } from '@spcc/core'

// ============================================
// Shared Schemas
// ============================================

export const localizedTextSchema = z.object({
  en: z.string(),
  ar: z.string().optional().default(''),
})

export const moneySchema = z.object({
  amount: z.number(),
  currency: z.enum(['SAR', 'USD']),
})

export const quantitySchema = z.object({
  value: z.number().positive(),
  unit: z.enum(['bags', 'tons', 'kg']),
})

// ============================================
// Order Schemas
// ============================================

export const orderStatusSchema = z.enum([
  'pending',
  'processing',
  'delivered',
  'cancelled',
])

export const orderItemDtoSchema = z.object({
  productId: z.string(),
  product: localizedTextSchema,
  quantity: quantitySchema,
  unitPrice: moneySchema,
  totalPrice: moneySchema,
})

export const orderDtoSchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  sapSalesOrderNumber: z.string().nullable().optional(),
  customerId: z.string(),
  contractId: z.string().optional(),
  items: z.array(orderItemDtoSchema),
  status: orderStatusSchema,
  processingStartedAt: z.string().optional(),
  orderDate: z.string(),
  deliveryDate: z.string(),
  totalAmount: moneySchema,
  deliveryAddress: z.string(),
  notes: z.string().optional(),
  subCustomer: z
    .object({
      id: z.union([z.string(), z.number()]).transform(String).optional(),
      documentId: z.string().optional(),
      name: z.string().optional(),
      phone: z.string().nullable().optional(),
      code: z.string().optional(),
    })
    .nullable()
    .optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const orderStatsDtoSchema = z.object({
  pending: z.number(),
  processing: z.number(),
  delivered: z.number(),
  total: z.number(),
})

// ============================================
// Request Schemas
// ============================================

export const createOrderRequestSchema = z.object({
  customerId: z.string().min(1, 'Customer ID is required'),
  contractId: z.string().optional(),
  items: z.array(
    z.object({
      productId: z.string(),
      product: localizedTextSchema,
      quantity: quantitySchema,
      unitPrice: moneySchema,
    })
  ).min(1, 'At least one item is required'),
  deliveryDate: z.string().min(1, 'Delivery date is required'),
  deliveryAddress: z.string().min(1, 'Delivery address is required'),
  notes: z.string().optional(),
})

export const updateOrderRequestSchema = z.object({
  deliveryDate: z.string().optional(),
  deliveryAddress: z.string().optional(),
  notes: z.string().optional(),
})

export const ordersFilterSchema = z.object({
  status: z.union([orderStatusSchema, z.literal('all')]).optional(),
  customerId: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  search: z.string().optional(),
})

// ============================================
// Response Schemas
// ============================================

export const ordersListResponseSchema = z.object({
  data: z.array(orderDtoSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
  }).optional(),
})

export const orderResponseSchema = z.object({
  data: orderDtoSchema,
})

// ============================================
// Inferred Types
// ============================================

export type OrderDto = z.infer<typeof orderDtoSchema>
export type OrderItemDto = z.infer<typeof orderItemDtoSchema>
export type OrderStatsDto = z.infer<typeof orderStatsDtoSchema>
export type CreateOrderRequestDto = z.infer<typeof createOrderRequestSchema>
export type UpdateOrderRequestDto = z.infer<typeof updateOrderRequestSchema>
export type OrdersFilterDto = z.infer<typeof ordersFilterSchema>
export type OrdersListResponse = z.infer<typeof ordersListResponseSchema>
export type OrderResponse = z.infer<typeof orderResponseSchema>

// ============================================
// Validation Helpers
// ============================================

export function validateOrderDto(data: unknown): OrderDto {
  return orderDtoSchema.parse(data)
}

export function validateOrdersResponse(data: unknown): OrderDto[] {
  // Handle both array and wrapped responses
  if (Array.isArray(data)) {
    return z.array(orderDtoSchema).parse(data)
  }
  const response = ordersListResponseSchema.parse(data)
  return response.data
}

export function validateOrderStats(data: unknown): OrderStatsDto {
  return orderStatsDtoSchema.parse(data)
}

// API Endpoints are defined in domain/constants/index.ts
